import { useMemo, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'
import { Button, Field, Meter, Stack, Text } from '@/design-system'
import { AuthHeading } from './AuthForm'

const strengthOf = (value: string): { readonly score: number; readonly label: string } => {
  let score = 0
  if (value.length >= 8) score += 1
  if (value.length >= 12) score += 1
  if (/[^a-zA-Z0-9]/.test(value)) score += 1
  if (/\d/.test(value) && /[a-z]/i.test(value)) score += 1
  const labels = ['Too short', 'Weak', 'Fair', 'Strong', 'Very strong']
  return { score: score / 4, label: labels[score] ?? 'Weak' }
}

export function ResetPasswordScreen() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmValue, setConfirmValue] = useState('')
  const strength = useMemo(() => strengthOf(password), [password])

  const mismatch = confirmValue.length > 0 && confirmValue !== password
  const valid = password.length >= 8 && !mismatch && confirmValue.length > 0

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (valid) navigate('/login', { replace: true })
  }

  return (
    <Stack as="form" gap={6} onSubmit={handleSubmit}>
      <AuthHeading
        title="Set a new password"
        subtitle="Eight characters at minimum. Longer beats complicated."
      />

      <Stack gap={3}>
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {password.length > 0 && (
          <Stack gap={2}>
            <Meter value={strength.score} height={5} tone="ink" label="Password strength" />
            <Text variant="eyebrow" tone="secondary">
              {strength.label}
            </Text>
          </Stack>
        )}
        <Field
          label="Repeat it"
          type="password"
          autoComplete="new-password"
          value={confirmValue}
          onChange={(event) => setConfirmValue(event.target.value)}
          {...(mismatch ? { error: 'The two do not match.' } : {})}
        />
      </Stack>

      <Button type="submit" size="lg" block disabled={!valid}>
        Save and log in
      </Button>
    </Stack>
  )
}
